import type { ProductGroupsData } from '@/entities/optimization-group'
import type { OptimizationGroupProductGroupMapping } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { TFunction } from 'i18next'

import React, { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { any, prop } from 'lodash/fp'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useStep } from 'usehooks-ts'

import {
	getSalesEntitiesTree,
	ProductGroupsPicker,
	useCreateOptimizationGroupMutation,
	useProductGroupCatalogQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'

import { TreeCheckbox } from '@/shared/components/TreePicker'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { ampli } from '@/shared/lib/ampli'
import { getCompactedSelection } from '@/shared/lib/tree'

import CheckDataView from './CheckDataView'

const animation = {
	in: { opacity: 0, x: 100 },
	visible: { opacity: 1, x: 0 },
	out: { opacity: 0, x: -30 },
}
type State = {
	selectedSalesEntities: string[]
	selectedProductGroups: OptimizationGroupProductGroupMapping[]
}
type CreateOptimizationGroupModalProps = {
	close(): void
	isOpen: boolean
}
const sleep = (t: number) => new Promise((r) => setTimeout(() => r(true), t))
function CreateOptimizationGroupModalContent({
	isOpen,
	handleClose,
}: {
	isOpen: boolean
	handleClose(): void
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [step, stepHelpers] = useStep(3)
	const steps = getSteps(t)
	const { Component: StepComponent, title, subtitle } = steps[step - 1]
	const [draftState, setDraftState] = useState<State>({
		selectedSalesEntities: [],
		selectedProductGroups: [],
	})
	const salesEntitiesQuery = useSalesEntitiesQuery()
	const productGroupsQuery = useProductGroupCatalogQuery()
	const createOptimizationGroupMutation = useCreateOptimizationGroupMutation({
		async onSuccess(data) {
			await sleep(2000)
			toast.success('Optimization group created')
			await queryClient.invalidateQueries({
				queryKey: ['optimization-groups'],
			})
			await queryClient.invalidateQueries({
				queryKey: ['optimizations'],
			})
			navigate({ to: `/p/og/${data.id}/settings` })
			ampli.createOgCloseModal()
			handleClose()
		},
	})
	const salesEntitiesTree = useMemo(
		() => getSalesEntitiesTree(salesEntitiesQuery.data),
		[salesEntitiesQuery.data]
	)
	const isLoading = any(prop('isLoading'), [
		salesEntitiesQuery,
		productGroupsQuery,
	])
	const isPending = any(prop('isPending'), [createOptimizationGroupMutation])
	const getSelectedSalesEntities = () =>
		getCompactedSelection(salesEntitiesTree, draftState.selectedSalesEntities)
	const isValidSalesItems = () => {
		const { level, selected } = getSelectedSalesEntities()

		return (
			!(level === 1 && selected.length > 1) &&
			draftState.selectedSalesEntities.length > 0
		)
	}
	const handleSubmit = () => {
		ampli.createOgSubmitForm()
		const { level, selected } = getSelectedSalesEntities()
		createOptimizationGroupMutation.mutate({
			sales_level_id: level!,
			description: null,
			sales_entities: selected.map(({ id }) => id),
			product_groups: draftState.selectedProductGroups,
		})
	}
	const errorMessage = (
		createOptimizationGroupMutation.error?.response?.data as any
	)?.error

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleClose()
				}
			}}
		>
			<ModalContent>
				{isLoading ? (
					<div className='flex h-100 w-full items-center justify-center'>
						<Loader />
					</div>
				) : (
					<>
						<ModalHeader>
							<ModalTitle>{title}</ModalTitle>
							<ModalDescription>{subtitle}</ModalDescription>
						</ModalHeader>
						<ModalBody className='relative flex h-100 flex-col overflow-y-auto p-0'>
							<Blanket
								isTinted={isPending}
								className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
							>
								<Loader />
							</Blanket>
							{createOptimizationGroupMutation.error && (
								<div className='m-5 flex flex-col'>
									<Alert variant='destructive'>
										<AlertDescription>
											{errorMessage || t('fatal_error_title')}
										</AlertDescription>
									</Alert>
								</div>
							)}
							<AnimatePresence mode='wait'>
								<motion.div
									key={`${step}_step`}
									className='flex flex-col p-4'
									initial='in'
									animate='visible'
									exit='out'
									variants={animation}
									transition={{
										duration: 0.2,
									}}
								>
									<StepComponent
										state={draftState}
										setState={setDraftState}
										salesEntitiesTree={salesEntitiesTree}
										salesLevels={salesEntitiesQuery.data?.sales_levels}
										productGroups={productGroupsQuery.data}
									/>
								</motion.div>
							</AnimatePresence>
						</ModalBody>
						<ModalFooter>
							<Button
								className='mr-auto'
								onClick={() => {
									ampli.createOgCloseModal()
									handleClose()
								}}
							>
								{t('general_cancel')}
							</Button>
							<Button
								disabled={!stepHelpers.canGoToPrevStep}
								onClick={() => {
									ampli.createOgGoPrevStep()
									stepHelpers.goToPrevStep()
								}}
							>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
							{stepHelpers.canGoToNextStep ? (
								<Button
									variant='primary'
									disabled={
										!stepHelpers.canGoToNextStep ||
										(step === 1 && !isValidSalesItems()) ||
										(step === 2 &&
											draftState.selectedProductGroups.length === 0)
									}
									onClick={() => {
										ampli.createOgGoNextStep()
										stepHelpers.goToNextStep()
									}}
								>
									{t('general_next')}
									<ChevronRightIcon />
								</Button>
							) : (
								<Button
									variant='primary'
									disabled={isPending}
									onClick={handleSubmit}
								>
									{t('app.create_og')}
								</Button>
							)}
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	)
}
export default function CreateOptimizationGroupModal({
	close,
	isOpen,
}: CreateOptimizationGroupModalProps) {
	return (
		<CreateOptimizationGroupModalContent isOpen={isOpen} handleClose={close} />
	)
}
const getSteps = (t: TFunction) => [
	{
		title: t('app.create_og_step_1.title'),
		subtitle: t('app.create_og_step_1.subtitle'),
		Component: SalesLevelTreeView,
	},
	{
		title: t('app.create_og_step_2.title'),
		subtitle: t('app.create_og_step_2.subtitle'),
		Component: ProductLevelTreeView,
	},
	{
		title: t('app.create_og_step_3.title'),
		subtitle: t('app.create_og_step_3.subtitle'),
		Component: CheckDataView,
	},
]

type ProductLevelTreeViewProps = {
	state: State
	setState: React.Dispatch<React.SetStateAction<State>>
	productGroups?: ProductGroupsData
}
function ProductLevelTreeView({
	state,
	setState,
	productGroups,
}: ProductLevelTreeViewProps) {
	if (!productGroups) return null

	return (
		<ProductGroupsPicker
			data={productGroups}
			value={state.selectedProductGroups}
			onChange={(items) =>
				setState((prevState) => ({
					...prevState,
					selectedProductGroups: items,
				}))
			}
		/>
	)
}
type SalesLevelTreeViewProps = {
	state: State
	setState: React.Dispatch<React.SetStateAction<State>>
	salesEntitiesTree: TreeNodeType[]
}
function SalesLevelTreeView({
	state,
	setState,
	salesEntitiesTree,
}: SalesLevelTreeViewProps) {
	if (salesEntitiesTree) {
		return (
			<TreeCheckbox
				tree={salesEntitiesTree}
				value={state.selectedSalesEntities}
				onChange={(items) =>
					setState((prevState) => ({
						...prevState,
						selectedSalesEntities: items,
					}))
				}
			/>
		)
	}
}
